import React, { useState, useEffect } from 'react';
import { Typography, Tabs, TabList, Tab, TabPanel, Switch, Select, Option } from '@mui/joy';
import { Card, Button, Input, Alert } from '@components/ui';
import { useAuth } from '@contexts/AuthContext';
import { authService } from '@services/auth.service';
import {
  UserCircleIcon,
  LockClosedIcon,
  BellIcon,
  MoonIcon,
} from '@heroicons/react/24/outline';

export const Settings: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Profile settings
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
  });

  // Security settings
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // App settings
  const [appSettings, setAppSettings] = useState({
    theme: 'auto',
    language: 'en',
    timezone: 'UTC',
    autoRefresh: true,
    soundAlerts: false,
  });

  // Security preferences
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorAuth: false,
    sessionTimeout: 30,
    loginAlerts: true,
    deviceTracking: true,
  });

  useEffect(() => {
    // Load settings from localStorage or API
    const savedSettings = localStorage.getItem('appSettings');
    if (savedSettings) {
      setAppSettings(JSON.parse(savedSettings));
    }
  }, []);

  const updateProfile = async () => {
    if (!profileData.name.trim()) {
      setError('Name is required');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await authService.updateProfile({
        name: profileData.name,
        email: profileData.email,
      });
      setSuccess('Profile updated successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword) {
      setError('Please fill in all password fields');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await authService.changePassword(passwordData.currentPassword, passwordData.newPassword);
      setSuccess('Password changed successfully');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err: any) {
      setError(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const saveAppSettings = () => {
    localStorage.setItem('appSettings', JSON.stringify(appSettings));
    setSuccess('Settings saved successfully');
  };

  const testNotification = async () => {
    try {
      // This would call an API to send a test notification
      setSuccess('Test notification sent');
    } catch (err: any) {
      setError('Failed to send test notification');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Typography level="h2" className="font-bold text-gray-900 dark:text-white">
          Settings
        </Typography>
        <Typography level="body-md" className="text-gray-600 dark:text-gray-400 mt-1">
          Manage your account and application preferences
        </Typography>
      </div>

      {error && (
        <Alert color="danger" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert color="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value as number)}>
        <TabList>
          <Tab>
            <UserCircleIcon className="w-4 h-4 mr-2" />
            Profile
          </Tab>
          <Tab>
            <LockClosedIcon className="w-4 h-4 mr-2" />
            Security
          </Tab>
          <Tab>
            <BellIcon className="w-4 h-4 mr-2" />
            Notifications
          </Tab>
          <Tab>
            <MoonIcon className="w-4 h-4 mr-2" />
            Appearance
          </Tab>
        </TabList>

        {/* Profile Tab */}
        <TabPanel value={0} className="pt-6">
          <Card className="p-6">
            <Typography level="h4" className="font-semibold mb-4">
              Profile Information
            </Typography>
            <div className="space-y-4">
              <Input
                label="Full Name"
                value={profileData.name}
                onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                required
                fullWidth
              />
              <Input
                label="Email Address"
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                required
                fullWidth
              />
              <div className="pt-2">
                <Button
                  onClick={updateProfile}
                  loading={loading}
                  variant="solid"
                >
                  Update Profile
                </Button>
              </div>
            </div>
          </Card>
        </TabPanel>

        {/* Security Tab */}
        <TabPanel value={1} className="pt-6">
          <div className="space-y-6">
            <Card className="p-6">
              <Typography level="h4" className="font-semibold mb-4">
                Change Password
              </Typography>
              <div className="space-y-4">
                <Input
                  label="Current Password"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                  required
                  fullWidth
                />
                <Input
                  label="New Password"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                  required
                  fullWidth
                />
                <Input
                  label="Confirm New Password"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  required
                  fullWidth
                />
                <div className="pt-2">
                  <Button
                    onClick={changePassword}
                    loading={loading}
                    variant="solid"
                  >
                    Change Password
                  </Button>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <Typography level="h4" className="font-semibold mb-4">
                Security Preferences
              </Typography>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Typography level="body-md" className="font-medium">
                      Two-Factor Authentication
                    </Typography>
                    <Typography level="body-sm" className="text-gray-500">
                      Add an extra layer of security to your account
                    </Typography>
                  </div>
                  <Switch
                    checked={securitySettings.twoFactorAuth}
                    onChange={(e) => setSecuritySettings(prev => ({ ...prev, twoFactorAuth: e.target.checked }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Typography level="body-md" className="font-medium">
                      Login Alerts
                    </Typography>
                    <Typography level="body-sm" className="text-gray-500">
                      Get notified of new login attempts
                    </Typography>
                  </div>
                  <Switch
                    checked={securitySettings.loginAlerts}
                    onChange={(e) => setSecuritySettings(prev => ({ ...prev, loginAlerts: e.target.checked }))}
                  />
                </div>

                <div>
                  <Typography level="body-md" className="font-medium mb-2">
                    Session Timeout
                  </Typography>
                  <Select
                    value={securitySettings.sessionTimeout}
                    onChange={(_, value) => setSecuritySettings(prev => ({ ...prev, sessionTimeout: value as number }))}
                  >
                    <Option value={15}>15 minutes</Option>
                    <Option value={30}>30 minutes</Option>
                    <Option value={60}>1 hour</Option>
                    <Option value={240}>4 hours</Option>
                    <Option value={480}>8 hours</Option>
                  </Select>
                </div>
              </div>
            </Card>
          </div>
        </TabPanel>

        {/* Notifications Tab */}
        <TabPanel value={2} className="pt-6">
          <Card className="p-6">
            <Typography level="h4" className="font-semibold mb-4">
              Notification Settings
            </Typography>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Typography level="body-md" className="font-medium">
                    Sound Alerts
                  </Typography>
                  <Typography level="body-sm" className="text-gray-500">
                    Play sounds for new notifications
                  </Typography>
                </div>
                <Switch
                  checked={appSettings.soundAlerts}
                  onChange={(e) => setAppSettings(prev => ({ ...prev, soundAlerts: e.target.checked }))}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Typography level="body-md" className="font-medium">
                    Auto Refresh
                  </Typography>
                  <Typography level="body-sm" className="text-gray-500">
                    Automatically refresh dashboard data
                  </Typography>
                </div>
                <Switch
                  checked={appSettings.autoRefresh}
                  onChange={(e) => setAppSettings(prev => ({ ...prev, autoRefresh: e.target.checked }))}
                />
              </div>

              <div className="pt-2">
                <Button onClick={testNotification} variant="outlined" size="sm">
                  Send Test Notification
                </Button>
              </div>
            </div>
          </Card>
        </TabPanel>

        {/* Appearance Tab */}
        <TabPanel value={3} className="pt-6">
          <Card className="p-6">
            <Typography level="h4" className="font-semibold mb-4">
              Appearance Settings
            </Typography>
            <div className="space-y-4">
              <div>
                <Typography level="body-md" className="font-medium mb-2">
                  Theme
                </Typography>
                <Select
                  value={appSettings.theme}
                  onChange={(_, value) => setAppSettings(prev => ({ ...prev, theme: value as string }))}
                >
                  <Option value="light">Light</Option>
                  <Option value="dark">Dark</Option>
                  <Option value="auto">Auto (System)</Option>
                </Select>
              </div>

              <div>
                <Typography level="body-md" className="font-medium mb-2">
                  Language
                </Typography>
                <Select
                  value={appSettings.language}
                  onChange={(_, value) => setAppSettings(prev => ({ ...prev, language: value as string }))}
                >
                  <Option value="en">English</Option>
                  <Option value="es">Español</Option>
                  <Option value="fr">Français</Option>
                  <Option value="de">Deutsch</Option>
                </Select>
              </div>

              <div>
                <Typography level="body-md" className="font-medium mb-2">
                  Timezone
                </Typography>
                <Select
                  value={appSettings.timezone}
                  onChange={(_, value) => setAppSettings(prev => ({ ...prev, timezone: value as string }))}
                >
                  <Option value="UTC">UTC</Option>
                  <Option value="America/New_York">Eastern Time</Option>
                  <Option value="America/Chicago">Central Time</Option>
                  <Option value="America/Denver">Mountain Time</Option>
                  <Option value="America/Los_Angeles">Pacific Time</Option>
                  <Option value="Europe/London">GMT</Option>
                  <Option value="Europe/Paris">Central European Time</Option>
                </Select>
              </div>

              <div className="pt-2">
                <Button onClick={saveAppSettings} variant="solid">
                  Save Settings
                </Button>
              </div>
            </div>
          </Card>
        </TabPanel>
      </Tabs>
    </div>
  );
};